import cn from 'classnames'
import { Switch, TimeRange } from '@forwardflow/ui-v2'
import { IAvailabilityDay, TDay } from '@forwardflow/types/onboarding'
import { DAY_SHORT, browserTimezone } from '../shell/format'

const DAYS: TDay[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/**
 * A compact week: tap a day to switch it on or off, one time range for
 * every day or one per day. Used for calling hours and appointment hours.
 */
export const HoursEditor = ({
   days,
   sameForAllDays,
   onChange,
   timezone,
}: {
   days: IAvailabilityDay[]
   sameForAllDays: boolean
   onChange: (days: IAvailabilityDay[], sameForAllDays: boolean) => void
   timezone?: string
}) => {
   const byDay = (day: TDay) =>
      days.find((d) => d.day === day) || { day, startTime: '09:00', endTime: '18:00', enabled: false }
   const full = DAYS.map(byDay)
   const first = full.find((d) => d.enabled) || full[0]

   const toggleDay = (day: TDay) =>
      onChange(
         full.map((d) => (d.day === day ? { ...d, enabled: !d.enabled } : d)),
         sameForAllDays
      )
   const setAll = ([startTime, endTime]: [string, string]) =>
      onChange(
         full.map((d) => ({ ...d, startTime: startTime || d.startTime, endTime: endTime || d.endTime })),
         true
      )
   const setOne = (day: TDay, [startTime, endTime]: [string, string]) =>
      onChange(
         full.map((d) =>
            d.day === day
               ? { ...d, startTime: startTime || d.startTime, endTime: endTime || d.endTime }
               : d
         ),
         false
      )

   return (
      <div className="space-y-3" data-testid="hours-editor">
         <div className="flex flex-wrap gap-1.5">
            {full.map((d) => (
               <button
                  key={d.day}
                  type="button"
                  onClick={() => toggleDay(d.day)}
                  aria-pressed={d.enabled}
                  className={cn(
                     'rounded-lg border px-3 py-1.5 text-sm transition-all',
                     d.enabled
                        ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.30)] text-white'
                        : 'border-white/15 bg-white/[0.03] text-white/50 hover:border-white/30'
                  )}
               >
                  {DAY_SHORT[d.day]}
               </button>
            ))}
         </div>
         <label className="flex items-center gap-2 text-sm text-white/80">
            <Switch
               size="sm"
               checked={sameForAllDays}
               onChange={(e) => onChange(full, e.currentTarget.checked)}
            />
            Same hours every day
         </label>
         {sameForAllDays ? (
            <div className="flex items-center gap-3">
               <span className="text-white/60 text-sm w-20">All days</span>
               <TimeRange
                  key="all"
                  value={[first.startTime, first.endTime]}
                  setValue={setAll}
               />
            </div>
         ) : (
            <div className="space-y-2">
               {full
                  .filter((d) => d.enabled)
                  .map((d) => (
                     <div key={d.day} className="flex items-center gap-3">
                        <span className="text-white/60 text-sm w-20">{DAY_SHORT[d.day]}</span>
                        <TimeRange
                           key={`${d.day}`}
                           value={[d.startTime, d.endTime]}
                           setValue={(v) => setOne(d.day, v)}
                        />
                     </div>
                  ))}
            </div>
         )}
         <p className="text-white/40 text-xs">
            Times are in {(timezone || browserTimezone() || 'your').replace(/_/g, ' ')} time.
         </p>
      </div>
   )
}
